import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit3, RefreshCw, X, Loader2 } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Textarea, Select } from './shared';
import { useToast } from '../../hooks/use-toast';

const CATS = ['general', 'voip', 'sip', 'white-label', 'virtual-numbers', 'infrastructure'];
const BLANK = { q: '', a: '', category: 'general', order: 0 };

export default function AdminFaqs() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/faqs');
      setItems(res.data || []);
    } catch (err) { toast({ title: 'Failed to load', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.q?.trim() || !editing.a?.trim()) return toast({ title: 'Question and answer required' });
    setSaving(true);
    try {
      const body = {
        q: editing.q.trim(), a: editing.a.trim(),
        category: editing.category || 'general',
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.patch(`/api/admin/faqs/${editing.id}`, body);
      else await api.post('/api/admin/faqs', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try { await api.delete(`/api/admin/faqs/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const grouped = items.reduce((acc, f) => {
    (acc[f.category] ||= []).push(f);
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        title="FAQs"
        subtitle="Frequently asked questions shown across the site"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><Plus size={14} /> Add FAQ</Btn>
          </div>
        }
      />
      {loading ? <Loading /> : Object.keys(grouped).length === 0 ? (
        <AdminCard><Empty /></AdminCard>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <AdminCard key={cat} title={cat.toUpperCase()} subtitle={`${list.length} question${list.length > 1 ? 's' : ''}`}>
              <div className="divide-y divide-white/5">
                {list.map((f) => (
                  <div key={f.id} className="py-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white">{f.q}</p>
                      <p className="text-sm text-slate-400 mt-1 line-clamp-2">{f.a}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Btn size="sm" variant="ghost" onClick={() => setEditing(f)}><Edit3 size={12} /></Btn>
                      <Btn size="sm" variant="danger" onClick={() => remove(f.id)}><Trash2 size={12} /></Btn>
                    </div>
                  </div>
                ))}
              </div>
            </AdminCard>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit FAQ' : 'New FAQ'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input label="Question *" value={editing.q} onChange={(e) => setEditing({ ...editing, q: e.target.value })} />
              <Textarea label="Answer *" rows={5} value={editing.a} onChange={(e) => setEditing({ ...editing, a: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Input label="Order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
